import { RefObject } from 'react';

type columnCount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type columnSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type Columns = { [key in columnSize]?: columnCount };

const getCols = (cols?: Columns): string => {
  if (!cols) return '';

  const css = Object.keys(cols).map((key) => {
    return `col-${key !== 'xs' ? key + '-' : ''}${cols[key as columnSize]}`;
  });

  return css.join(' ');
};

interface TextInputProps {
  label?: string;
  type?: string;
  helpText?: React.ReactNode;
  name: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  value?: string | number;
  onChange?(e: React.ChangeEvent<HTMLInputElement>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement>): void;
  minLength?: number;
  maxLength?: number;
  inputCols?: Columns;
  labelCols?: Columns;
  className?: string;
  disableAutoComplete?: boolean;
  refObject?: RefObject<HTMLInputElement>;
  defaultValue?: string | number;
}

const TextInput = ({
  label = '',
  type = 'text',
  helpText = '',
  name = '',
  id = '',
  required = false,
  disabled = false,
  error = false,
  autoFocus = false,
  placeholder = '',
  value,
  onChange,
  onBlur,
  minLength,
  maxLength,
  inputCols,
  labelCols,
  className = '',
  disableAutoComplete = false,
  refObject,
  defaultValue
}: TextInputProps) => {
  const hasColumns = inputCols && labelCols;

  const helpId = id ? `${id}-help` : '';

  const labelColCss = getCols(labelCols);
  const inputColCss = getCols(inputCols);

  const requiredMarker = required ? (
    <>
      &nbsp;
      <span className="text-danger">*</span>
    </>
  ) : null;

  const helpTextClass = error
    ? 'form-text text-danger'
    : 'form-text text-muted';

  const labelHtml = label && (
    <label
      className={'form-control-label ' + labelColCss ? 'col-form-label' : ''}
      htmlFor={id}
    >
      {label}
      {requiredMarker}
    </label>
  );

  const input = (
    <input
      required={required}
      disabled={disabled}
      id={id}
      type={type}
      name={name}
      className={`form-control ${error ? 'form-control-danger' : ''}`}
      aria-describedby={helpId}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      minLength={minLength}
      maxLength={maxLength}
      autoFocus={autoFocus}
      autoComplete={disableAutoComplete ? 'off' : ''}
      ref={refObject}
      defaultValue={defaultValue}
    />
  );

  const helpTextHtml = helpText && (
    <small id={helpId} className={helpTextClass}>
      {helpText}
    </small>
  );

  return (
    <div
      className={`form-group ${error ? 'has-danger' : ''} ${
        hasColumns ? 'row' : ''
      } ${className}`}
    >
      {hasColumns ? <div className={labelColCss}>{labelHtml}</div> : labelHtml}

      {hasColumns ? (
        <div className={inputColCss}>
          {input}
          {helpTextHtml}
        </div>
      ) : (
        input
      )}

      {!hasColumns && helpTextHtml}
    </div>
  );
};

export default TextInput;
